<template>
  <header class="nerv-topbar">
    <div class="nerv-page-frame nerv-topbar-inner">
      <router-link to="/" class="brand" aria-label="Dev1 Consulting home">
        <span class="brand-mark">DEV1</span>
        <span class="brand-sub nerv-caption">Consulting</span>
      </router-link>

      <nav class="nerv-nav" aria-label="Primary">
        <router-link
          v-for="item in nav"
          :key="item.to"
          :to="item.to"
          class="nerv-nav-link"
          :class="{ 'is-active': isActive(item.to) }"
        >
          {{ item.label }}
        </router-link>
      </nav>

      <!-- The frame-rate readout lived here, but a site claiming production
           engineering rigour should not advertise its own frame drops in the
           header on a slower machine. It stays inside the console, where it is
           framed as a measurement rather than a scoreboard. -->
      <div class="readout">
        <span class="nerv-data-label">{{ code }}</span>
        <span class="nerv-caption clock">{{ clock }}</span>
      </div>

      <button
        class="menu-toggle"
        type="button"
        :aria-expanded="open"
        aria-label="Toggle navigation"
        @click="open = !open"
      >
        <span></span><span></span><span></span>
      </button>
    </div>

    <nav v-if="open" class="mobile-nav" aria-label="Primary mobile">
      <router-link
        v-for="item in nav"
        :key="item.to"
        :to="item.to"
        class="nerv-nav-link"
        :class="{ 'is-active': isActive(item.to) }"
        @click="open = false"
      >
        {{ item.label }}
      </router-link>
    </nav>
  </header>
</template>

<script lang="ts">
import { defineComponent } from 'vue';
// No telemetry subscription here any more: the footer ticker in App.vue is
// always mounted and keeps the shared sampler alive on its own.

export default defineComponent({
  name: 'NervTopBar',
  data() {
    return {
      open: false,
      clock: '',
      timer: 0,
      nav: [
        // "Operator" is the in-page flavour, but a recruiter scanning the nav is
        // looking for the word resume. The section label inside the page keeps
        // the console conceit.
        { to: '/', label: 'Overview' },
        { to: '/about', label: 'Resume' },
        { to: '/contact', label: 'Contact' },
      ],
    };
  },
  computed: {
    code(): string {
      return `SEC-${(this.$route.meta.code as string) || '00'}`;
    },
  },
  methods: {
    isActive(to: string): boolean {
      return to === '/' ? this.$route.path === '/' : this.$route.path.startsWith(to);
    },
    tick() {
      const d = new Date();
      const p = (n: number) => String(n).padStart(2, '0');
      this.clock = `${p(d.getHours())}:${p(d.getMinutes())}:${p(d.getSeconds())}`;
    },
  },
  mounted() {
    this.tick();
    this.timer = window.setInterval(this.tick, 1000);
  },
  beforeUnmount() {
    window.clearInterval(this.timer);
  },
});
</script>

<style scoped>
.brand {
  display: flex;
  align-items: baseline;
  gap: 0.5rem;
}

.brand-mark {
  font-family: var(--font-nerv-display);
  font-size: 1.15rem;
  font-weight: 700;
  letter-spacing: 0.3em;
  color: var(--nerv-orange);
  text-shadow: 0 0 8px rgba(255, 153, 0, 0.35);
}

.brand-sub {
  letter-spacing: 0.32em;
}

.readout {
  display: flex;
  align-items: center;
  gap: 0.75rem;
}

.clock {
  font-variant-numeric: tabular-nums;
  color: var(--nerv-orange);
}

.fps {
  font-variant-numeric: tabular-nums;
  color: var(--nerv-lcd-green);
}

.menu-toggle {
  display: none;
  flex-direction: column;
  gap: 4px;
  padding: 0.5rem;
  border: 1px solid var(--line-strong);
  background: transparent;
  cursor: pointer;
}

.menu-toggle span {
  display: block;
  width: 18px;
  height: 2px;
  background: var(--nerv-orange);
}

.mobile-nav {
  display: none;
  flex-direction: column;
  border-top: 1px solid var(--line-soft);
  background: rgba(0, 0, 0, 0.96);
}

@media (max-width: 820px) {
  .nerv-nav,
  .readout {
    display: none;
  }

  .menu-toggle,
  .mobile-nav {
    display: flex;
  }
}
</style>
